import React, { useState, useEffect } from 'react';
import { SocialLink } from '../../types';
import { firebaseService } from '../../services/firebaseService';
import {
  Plus, Trash2, Edit2, Save, X, Instagram, Facebook,
  Youtube, Twitter, Linkedin, Github, Music, Globe,
  ArrowUp, ArrowDown, ExternalLink, Loader2, Eye, EyeOff, ShieldCheck, Play
} from 'lucide-react';
import { useActionFlow } from '../ActionModal';

export const SocialLinksTab: React.FC = () => {
  const { startAction, openAlert } = useActionFlow();
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<SocialLink, 'id'>>({
    platform: 'instagram',
    label: '',
    url: '',
    isActive: true,
    sortOrder: 0
  });

  const [landingVideos, setLandingVideos] = useState<{ id: string; label: string; url: string; order: number; storagePath: string }[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoLabel, setVideoLabel] = useState('');
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);

  useEffect(() => {
    loadLinks();
    firebaseService.getLandingVideos().then(setLandingVideos);
  }, []);

  const loadLinks = async () => {
    setIsLoading(true);
    const data = await firebaseService.getSocialLinks();
    setLinks(data);
    setIsLoading(false);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram': return <Instagram size={18} />;
      case 'facebook': return <Facebook size={18} />;
      case 'youtube': return <Youtube size={18} />;
      case 'x': return <Twitter size={18} />;
      case 'linkedin': return <Linkedin size={18} />;
      case 'github': return <Github size={18} />;
      case 'tiktok': return <Music size={18} />;
      default: return <Globe size={18} />;
    }
  };

  const validateUrl = (url: string) => {
    return url.trim().toLowerCase().startsWith('https://');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateUrl(form.url)) {
      openAlert({
        title: 'Protocol Violation',
        message: 'Security Protocol Error: External links MUST utilize secure HTTPS protocols.',
        variant: 'warning'
      });
      return;
    }

    startAction({
      title: editingId ? 'Modify Shard' : 'Provision Node',
      message: `Confirm configuration for [${form.platform.toUpperCase()}]: ${form.label}?`,
      confirmLabel: editingId ? 'Update' : 'Provision',
      variant: 'info',
      action: async () => {
        if (editingId) {
          await firebaseService.updateSocialLink(editingId, form);
        } else {
          const nextOrder = links.length > 0 ? Math.max(...links.map(l => l.sortOrder)) + 1 : 0;
          await firebaseService.createSocialLink({ ...form, sortOrder: nextOrder });
        }
        setEditingId(null);
        setForm({ platform: 'instagram', label: '', url: '', isActive: true, sortOrder: 0 });
        await loadLinks();
      },
      successMessage: 'Network node updated.'
    });
  };

  const handleEdit = (link: SocialLink) => {
    setEditingId(link.id);
    setForm({ 
      platform: link.platform, 
      label: link.label, 
      url: link.url, 
      isActive: link.isActive, 
      sortOrder: link.sortOrder 
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    startAction({
      title: 'Decommission Node',
      message: 'This will permanently purge this social link from the public interface. Proceed?',
      confirmLabel: 'Purge Node',
      variant: 'danger',
      action: async () => {
        await firebaseService.deleteSocialLink(id);
        await loadLinks();
      },
      successMessage: 'Node decommissioned.'
    });
  };

  const toggleStatus = async (link: SocialLink) => {
    await firebaseService.updateSocialLink(link.id, { isActive: !link.isActive });
    await loadLinks();
  };

  const moveOrder = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= links.length) return;

    const newLinks = [...links];
    const currentItem = newLinks[index];
    const targetItem = newLinks[targetIndex];

    // Swap orders
    await firebaseService.updateSocialLink(currentItem.id, { sortOrder: targetItem.sortOrder });
    await firebaseService.updateSocialLink(targetItem.id, { sortOrder: currentItem.sortOrder });
    await loadLinks();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <p className="text-[10px] font-black uppercase tracking-widest">Scanning Intel Network...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        
        {/* Management Form */}
        <div className="lg:col-span-1">
          <div className="glass p-8 rounded-[2.5rem] border border-vanguard-border space-y-8 sticky top-24">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600/10 text-blue-600 rounded-2xl">
                <ExternalLink size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-vanguard-text uppercase tracking-tight">
                  {editingId ? 'Modify Shard' : 'Provision Node'}
                </h3>
                <p className="text-[9px] font-bold text-vanguard-muted uppercase tracking-[0.3em] mt-1">Presence Registry v1.0</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Platform Identity</label>
                <select 
                  value={form.platform}
                  onChange={e => setForm({...form, platform: e.target.value as any})}
                  className="w-full bg-vanguard-bg border border-vanguard-border rounded-xl px-4 py-4 text-sm font-bold focus:border-blue-500 outline-none transition-all cursor-pointer"
                >
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="x">X / Twitter</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="github">GitHub</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Public Label</label>
                <input 
                  value={form.label}
                  onChange={e => setForm({...form, label: e.target.value})}
                  placeholder="e.g., Tactical Insights on IG"
                  className="w-full bg-vanguard-bg border border-vanguard-border rounded-xl px-4 py-4 text-sm font-bold focus:border-blue-500 outline-none transition-all"
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest">Secure Target (HTTPS)</label>
                  {form.url && !validateUrl(form.url) && (
                    <span className="text-[8px] font-black text-rose-500 uppercase tracking-widest">Protocol Required</span>
                  )}
                </div>
                <input 
                  value={form.url}
                  onChange={e => setForm({...form, url: e.target.value})}
                  placeholder="https://platform.com/profile"
                  className={`w-full bg-vanguard-bg border rounded-xl px-4 py-4 text-sm font-bold focus:border-blue-500 outline-none transition-all ${form.url && !validateUrl(form.url) ? 'border-rose-500/50' : 'border-vanguard-border'}`}
                  required
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-vanguard-bg rounded-xl border border-vanguard-border">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className={form.isActive ? 'text-emerald-500' : 'text-vanguard-muted'} />
                  <span className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest">Live Status</span>
                </div>
                <button 
                  type="button"
                  onClick={() => setForm({...form, isActive: !form.isActive})}
                  className={`w-12 h-6 rounded-full transition-all relative ${form.isActive ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-slate-300 dark:bg-slate-800'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${form.isActive ? 'right-1' : 'left-1'}`} />
                </button>
              </div>

              <div className="flex gap-4 pt-4">
                {editingId && (
                  <button 
                    type="button" 
                    onClick={() => { setEditingId(null); setForm({ platform: 'instagram', label: '', url: '', isActive: true, sortOrder: 0 }); }}
                    className="flex-1 py-4 bg-vanguard-surface border border-vanguard-border text-vanguard-muted rounded-xl text-[10px] font-black uppercase tracking-widest hover:text-vanguard-text transition-all"
                  >
                    Abort
                  </button>
                )}
                <button 
                  className="flex-[2] py-4 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-blue-600/30 hover:bg-blue-500 transition-all active:scale-95 disabled:opacity-50"
                >
                  {editingId ? 'Sync Updates' : 'Provision Node'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Shard Registry */}
        <div className="lg:col-span-2">
          <div className="glass rounded-[3rem] overflow-hidden border border-vanguard-border shadow-2xl">
            <div className="p-8 bg-vanguard-surface/50 border-b border-vanguard-border flex justify-between items-center">
               <div className="space-y-1">
                 <h4 className="text-xs font-black uppercase tracking-[0.3em] flex items-center gap-3">
                   <Globe size={16} className="text-blue-600"/> Intel Shard Registry
                 </h4>
                 <p className="text-[9px] font-mono text-vanguard-muted uppercase ml-7">System.Registry.SocialNodes</p>
               </div>
               <div className="px-3 py-1 bg-black/5 dark:bg-white/5 rounded-full">
                 <span className="text-[10px] font-mono font-bold text-blue-600 uppercase">{links.length} Active Nodes</span>
               </div>
            </div>
            
            <div className="divide-y divide-vanguard-border">
              {links.length === 0 ? (
                <div className="p-32 text-center space-y-6 opacity-20">
                  <Globe size={64} className="mx-auto" />
                  <p className="text-sm font-black uppercase tracking-[0.4em]">Registry Empty. Deploy initial nodes.</p>
                </div>
              ) : links.map((link, index) => (
                <div key={link.id} className="p-6 flex items-center justify-between hover:bg-blue-600/[0.02] transition-all group">
                  <div className="flex items-center gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-vanguard-bg border border-vanguard-border flex items-center justify-center text-blue-600 shadow-sm group-hover:border-blue-500/30 transition-all">
                      {getPlatformIcon(link.platform)}
                    </div>
                    <div className="space-y-1">
                      <h5 className="text-sm font-black text-vanguard-text uppercase tracking-tight">{link.label}</h5>
                      <div className="flex items-center gap-3">
                        <code className="text-[9px] font-mono text-vanguard-muted bg-black/5 dark:bg-white/5 px-2 py-0.5 rounded uppercase">{link.platform}</code>
                        <span className="w-1 h-1 rounded-full bg-vanguard-border" />
                        <span className={`text-[8px] font-black uppercase tracking-widest ${link.isActive ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {link.isActive ? '• Broadcasting' : '• Offline'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 pr-3 border-r border-vanguard-border opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 duration-300">
                      <button 
                        onClick={() => moveOrder(index, 'up')}
                        disabled={index === 0}
                        className="p-2 hover:bg-blue-600 hover:text-white rounded-lg transition-all disabled:opacity-10"
                        title="Move Priority Up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button 
                        onClick={() => moveOrder(index, 'down')}
                        disabled={index === links.length - 1}
                        className="p-2 hover:bg-blue-600 hover:text-white rounded-lg transition-all disabled:opacity-10"
                        title="Move Priority Down"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => toggleStatus(link)}
                        className={`p-3 rounded-xl transition-all ${link.isActive ? 'text-emerald-500 hover:bg-emerald-500/10' : 'text-vanguard-muted hover:bg-emerald-500/10 hover:text-emerald-500'}`}
                        title={link.isActive ? "Deactivate Link" : "Activate Link"}
                      >
                        {link.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button 
                        onClick={() => handleEdit(link)}
                        className="p-3 hover:bg-blue-500/10 text-vanguard-muted hover:text-blue-500 rounded-xl transition-all"
                        title="Edit Node"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        onClick={() => handleDelete(link.id)}
                        className="p-3 hover:bg-rose-500/10 text-vanguard-muted hover:text-rose-500 rounded-xl transition-all"
                        title="Purge Shard"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 flex items-center gap-3 px-6 py-4 bg-blue-600/5 rounded-2xl border border-blue-600/10">
            <ShieldCheck size={16} className="text-blue-600" />
            <p className="text-[10px] font-bold text-vanguard-muted uppercase tracking-widest">Registry updates are replicated across the global Vanguard CDN nodes instantly.</p>
          </div>
        </div>
      </div>

      {/* Explainer Videos */}
      <div className="glass p-8 rounded-[2.5rem] border border-vanguard-border space-y-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600/10 text-blue-600 rounded-2xl">
            <Play size={24} />
          </div>
          <div>
            <h3 className="text-xl font-black text-vanguard-text uppercase tracking-tight">Explainer Videos</h3>
            <p className="text-[9px] font-bold text-vanguard-muted uppercase tracking-[0.3em] mt-1">Landing Page Carousel</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upload Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Label</label>
              <input
                type="text"
                value={videoLabel}
                onChange={e => setVideoLabel(e.target.value)}
                placeholder="e.g. Product Overview"
                className="w-full bg-vanguard-bg border border-vanguard-border rounded-xl px-4 py-3 text-sm font-bold focus:border-blue-500 outline-none transition-all"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-vanguard-muted uppercase tracking-widest ml-1">Video File (MP4)</label>
              <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-vanguard-border rounded-2xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all">
                <Play size={20} className="text-vanguard-muted mb-1" />
                <p className="text-xs font-bold text-vanguard-muted">
                  {videoFile ? videoFile.name : 'Click to select MP4'}
                </p>
                {videoFile && (
                  <p className="text-[10px] text-vanguard-muted mt-0.5">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                )}
                <input type="file" accept="video/mp4,video/*" className="hidden"
                  onChange={e => { setVideoFile(e.target.files?.[0] || null); setVideoError(null); }} />
              </label>
            </div>

            {videoProgress !== null && (
              <div className="space-y-1">
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${videoProgress}%` }} />
                </div>
                <p className="text-[10px] text-vanguard-muted text-right">{videoProgress}%</p>
              </div>
            )}

            {videoError && <p className="text-xs text-rose-500 font-bold">{videoError}</p>}

            <button
              disabled={!videoFile || !videoLabel.trim() || videoProgress !== null}
              onClick={async () => {
                if (!videoFile || !videoLabel.trim()) return;
                setVideoError(null);
                try {
                  await firebaseService.uploadLandingVideo(videoFile, videoLabel.trim(), setVideoProgress);
                  const updated = await firebaseService.getLandingVideos();
                  setLandingVideos(updated);
                  setVideoFile(null);
                  setVideoLabel('');
                  setVideoProgress(null);
                } catch (e: any) {
                  setVideoError(e.message || 'Upload failed');
                  setVideoProgress(null);
                }
              }}
              className="w-full py-4 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Play size={14} /> Upload Video
            </button>
          </div>

          {/* Published Videos */}
          <div className="lg:col-span-2">
            {landingVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-12 opacity-30 gap-3">
                <Play size={32} />
                <p className="text-[10px] font-black uppercase tracking-widest">No videos uploaded yet.</p>
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 scrollbar-hide">
                {landingVideos.map(video => (
                  <div key={video.id} className="snap-start shrink-0 w-72 rounded-2xl overflow-hidden border border-vanguard-border shadow-md bg-vanguard-bg">
                    <video src={video.url} autoPlay muted loop playsInline className="w-full" />
                    <div className="px-4 py-3 border-t border-vanguard-border flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-vanguard-text truncate">{video.label}</p>
                      <button
                        onClick={async () => {
                          await firebaseService.deleteLandingVideo(video.id, video.storagePath);
                          setLandingVideos(prev => prev.filter(v => v.id !== video.id));
                        }}
                        className="shrink-0 p-1.5 text-vanguard-muted hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};